// AGC: LoteCardMini - Square mini LOD card for dashboard horizontal scroll
// AGC: Condensed version with image, progress, name, and weather info
// AGC: DJI Action 5 Pro inspired horizontal scrolling design
// AGC: Now shows temperature and accumulated rain (24h) instead of health indicator

import React, { useState, useMemo } from 'react'
import { Thermometer, CloudRain } from 'lucide-react'
import { CircularProgress } from '../common/CircularProgress'
import { getCropDisplayInfo } from '../../types/field'
import { calculateLotProgress } from '../../utils/lotProgress'
import type { Plot } from '../../types'

// AGC: Weather data for lot card (will be fetched from API in future)
interface LotWeatherData {
  temperature: number       // AGC: Current temperature in °C
  rain24h: number          // AGC: Accumulated rain last 24h in mm
}

interface LoteCardMiniProps {
  lot: Plot
  onClick: () => void
  imageUrl?: string | null
  isImageLoading?: boolean
  weather?: LotWeatherData | null  // AGC: Weather data for the lot location
  // AGC: compact=true on desktop: 128px cards (3 fit in left half column) vs default 160px
  compact?: boolean
}

/**
 * AGC: Mini square LOD card for dashboard
 * AGC: Features:
 * AGC: - Square aspect ratio (160x160px)
 * AGC: - Full-bleed crop image background
 * AGC: - Small circular progress (32px)
 * AGC: - Weather info (temperature + rain 24h)
 * AGC: - Optimized for horizontal scroll
 */
export const LoteCardMini: React.FC<LoteCardMiniProps> = ({
  lot,
  onClick,
  imageUrl,
  isImageLoading = false,
  weather,
  compact = false,
}) => {
  const [imageError, setImageError] = useState(false)
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const progress = useMemo(() => calculateLotProgress(lot), [lot])
  
  // AGC: Determine if we should show image or fallback
  const showImage = imageUrl && !imageError && !isImageLoading
  const showFallback = !showImage
  
  // AGC: Real weather data from OpenMeteo via useLotCurrentWeather hook
  const weatherData = weather
  
  return (
    <div className="flex-shrink-0">
      <button
        onClick={onClick}
        className={`${compact ? 'w-32 h-32' : 'w-40 h-40'} rounded-xl overflow-hidden relative text-left transition-all duration-200 hover:scale-105 hover:shadow-xl active:scale-95 group shadow-md`}
      >
      {/* AGC: Background Image or Fallback */}
      {isImageLoading ? (
        <div className="absolute inset-0 bg-slate-700 animate-pulse flex items-center justify-center">
          <span className="text-3xl">{cropDisplay.icon}</span>
        </div>
      ) : showFallback ? (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
          <span className="text-4xl">{cropDisplay.icon}</span>
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={cropDisplay.name}
          onError={() => setImageError(true)}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      )}

      {/* AGC: Dark Gradient Overlay (lighter than full card) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

      {/* AGC: Content */}
      <div className="relative z-10 h-full p-2.5 flex flex-col justify-between">
        {/* AGC: Top row: Weather info + Progress */}
        <div className="flex justify-between items-start">
          {/* AGC: Weather info (temperature + rain) — real data from OpenMeteo */}
          {weatherData && (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded px-1.5 py-0.5">
                <Thermometer className="w-3 h-3 text-amber-300" />
                <span className="text-[10px] font-semibold text-white">{weatherData.temperature}°</span>
              </div>
              <div className="flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded px-1.5 py-0.5">
                <CloudRain className="w-3 h-3 text-blue-300" />
                <span className="text-[10px] font-semibold text-white">{weatherData.rain24h}mm</span>
              </div>
            </div>
          )}
          
          {/* AGC: Circular progress */}
          <CircularProgress 
            progress={progress} 
            size={32}
            strokeWidth={3}
            showLabel={true}
            progressColor="#10B981"
            bgColor="rgba(255,255,255,0.3)"
            labelColor="#fff"
          />
        </div>

        {/* AGC: Bottom: LOT name + hectare size */}
        <div>
          <h3 className="text-sm font-bold text-white truncate leading-tight drop-shadow-lg">
            {lot.name}
          </h3>
          <p className="text-[10px] text-white/90 font-medium drop-shadow">
            {lot.area.toFixed(1)} ha
          </p>
        </div>
      </div>

      {/* AGC: Hover glow effect */}
      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-200" />
    </button>
    </div>
  )
}
